import React from 'react';
import { FaUsers } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';

/** `error` is a catalog descriptor `{ key, params }` from the hook, or plain text. */
const errorText = (error, t) => (typeof error === 'string' ? error : t(error.key, error.params));

/**
 * "Predicted for tonight" card (Figma Z09): upcoming events with their expected
 * start. Each item carries the event `type` from `/me/predicted-events`
 * ("scheduled" today; inferred kinds later) as `data-event-type`, so kinds can
 * be told apart without a component change. The copy is the same for all of
 * them: under this heading every entry reads as a prediction.
 */
const HomePredictedCard = ({ events = [], loading = false, error = null, onRetry, onSeeAll, onOpenBand }) => {
  const { t, format } = useLocale();

  const renderBody = () => {
    if (loading) {
      // Not the empty copy: "nothing tonight" is a claim, and the answer is
      // not in yet.
      return (
        <p className="listener-home-card-empty" role="status">{t('home.predicted.loading')}</p>
      );
    }
    if (error) {
      return (
        <div className="listener-home-card-error">
          <p className="listener-home-card-empty" role="alert">{errorText(error, t)}</p>
          {onRetry && (
            <button type="button" className="listener-home-text-link" onClick={onRetry}>
              {t('home.predicted.tryAgain')}
            </button>
          )}
        </div>
      );
    }
    if (events.length === 0) {
      return <p className="listener-home-card-empty">{t('home.predicted.empty')}</p>;
    }
    return (
      <ul className="listener-home-predicted-list">
        {events.map((event) => (
          <li key={event.id} className="listener-home-predicted-item" data-event-type={event.type ?? undefined}>
            <button
              type="button"
              className="listener-home-predicted-tile"
              onClick={() => onOpenBand?.(event.band)}
              aria-label={t('home.predicted.openBand', { name: event.band.Name })}
            >
              {event.band.ProfileImageURL ? (
                <img src={event.band.ProfileImageURL} alt="" className="listener-home-predicted-art" />
              ) : (
                <FaUsers className="listener-home-predicted-art-fallback" aria-hidden="true" />
              )}
            </button>
            <span className="listener-home-predicted-name">{event.band.Name}</span>
            <time className="listener-home-eyebrow" dateTime={event.startsAt.toISOString()}>
              {t('home.predicted.likely', {
                date: format.date(event.startsAt, 'weekdayMonthDay'),
                time: format.time(event.startsAt),
              })}
            </time>
            {event.location && <span className="listener-home-predicted-location">{event.location}</span>}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="listener-home-card listener-home-predicted" aria-labelledby="listener-home-predicted-title" data-testid="home-predicted">
      <div className="listener-home-card-header">
        <h2 id="listener-home-predicted-title" className="listener-home-card-title">{t('home.predicted.title')}</h2>
        <button type="button" className="listener-home-text-link" onClick={onSeeAll}>
          {t('home.predicted.seeAll')}
        </button>
      </div>
      {renderBody()}
    </section>
  );
};

export default HomePredictedCard;
